import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, MessageSquare, DollarSign, Target, FileText } from 'lucide-react';
import Navigation from '@/components/Navigation';
import DashboardHeader from '@/components/DashboardHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { useCampaignDetails } from '@/hooks/query/useCampaignDetails';
import CreateTemplateDialog from '@/components/sms/templates/CreateTemplateDialog';
import { useAuth } from '@/context/auth/useAuth';
import { toast } from '@/hooks/use-toast';
import { AttributionWindowSelector } from '@/components/sms/campaign/AttributionWindowSelector';
import { CampaignPromoPerformanceComponent } from '@/components/sms/campaign/CampaignPromoPerformance';
import { useCampaignPromoPerformance } from '@/hooks/sms/useCampaignPromoPerformance';

const CampaignDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State for template dialog
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  
  // State for attribution window
  const [attributionWindow, setAttributionWindow] = useState<number | null>(null);
  
  // Fetch campaign details using the hook
  const { data: campaign, isLoading, isError, error } = useCampaignDetails(id);
  
  // Fetch promo performance data if campaign has promo code and is sent
  const {
    performance,
    isLoading: isPerformanceLoading,
    error: performanceError,
    refetch: refetchPerformance
  } = useCampaignPromoPerformance(
    campaign?.promo_code_id && campaign?.schedule_time ? parseInt(id || '0') : null
  );

  const getStatusBadge = (status: string) => {
    const variants = {
      'draft': 'secondary',
      'scheduled': 'outline', 
      'sending': 'default',
      'sent': 'default',
      'failed': 'destructive'
    } as const;
    
    return <Badge variant={variants[status as keyof typeof variants] || 'default'}>{status}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Handle template creation
  const handleSaveAsTemplate = () => {
    if (!campaign?.message) {
      toast({
        title: "No message content",
        description: "This campaign doesn't have any message content to save as a template.",
        variant: "destructive"
      });
      return;
    }
    setIsTemplateDialogOpen(true);
  };

  // Handle successful template creation
  const handleTemplateCreated = () => {
    toast({
      title: "Template saved",
      description: `Campaign message has been saved as a template successfully.`,
    });
    setIsTemplateDialogOpen(false);
  };

  // Handle attribution window update
  const handleAttributionWindowUpdate = (newWindow: number | null) => {
    setAttributionWindow(newWindow);
    // Refetch performance data with new attribution window
    refetchPerformance();
  };


  if (isLoading) {
    return (
      <Navigation>
        <div className="space-y-6">
          <DashboardHeader 
            title="Campaign Details" 
            description="Loading campaign information..." 
          />
          <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10" />
              <Skeleton className="h-8 w-64" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
            <Skeleton className="h-64" />
          </div>
        </div>
      </Navigation>
    );
  }

  // Handle error state
  if (isError || (!isLoading && !campaign)) {
    return (
      <Navigation>
        <div className="space-y-6">
          <DashboardHeader 
            title="Campaign Not Found" 
            description="The campaign you're looking for doesn't exist" 
          />
          <div className="container mx-auto py-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold mb-4">Campaign Not Found</h1>
              <p className="text-muted-foreground mb-4">
                {error instanceof Error ? error.message : "The campaign you're looking for doesn't exist."}
              </p>
              <Button onClick={() => navigate('/sms-campaigns')}>
                Back to Campaigns
              </Button>
            </div>
          </div>
        </div>
      </Navigation>
    );
  }

  // Don't render if still loading or no campaign data
  if (!campaign) {
    return null;
  }

  return (
    <Navigation>
      <div className="space-y-6">
        <div className="container mx-auto pt-2 pb-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sms-campaigns')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{campaign.name}</h1>
              <p className="text-muted-foreground">{campaign.restaurant.name}</p>
            </div>
            {getStatusBadge(campaign.status)}
          </div>
          <Button
            onClick={handleSaveAsTemplate}
            className="gap-2"
            variant="outline"
          >
            <FileText className="h-4 w-4" />
            Save as Template
          </Button>
        </div>

        {/* Promo Code Performance Section */}
        {campaign?.schedule_time && campaign?.promo_code_id && (
          <div className="space-y-6">
            {performance && (
              <CampaignPromoPerformanceComponent
                performance={performance}
                totalRecipients={campaign.recipients}
                totalOptOuts={campaign.optOuts}
                campaignId={id}
                isLoading={isPerformanceLoading}
                error={performanceError}
              />
            )}
          </div>
        )}

        {/* Message Content and Attribution Window Settings */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Message Content</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-muted p-4 rounded-lg">
                  <p className="whitespace-pre-wrap">{campaign.message}</p>
                  <p className="mt-2 text-xs text-muted-foreground border-t pt-2">
                    Reply STOP to optout
                  </p>
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  Character count: {campaign.message.length + 22}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Attribution Window Settings */}
          {campaign?.schedule_time && campaign?.promo_code_id && (
            <div className="lg:col-span-1">
              <AttributionWindowSelector
                campaignId={parseInt(id || '0')}
                currentWindow={attributionWindow !== null ? attributionWindow : campaign.attribution_window_days}
                scheduledAt={campaign.schedule_time}
                onWindowUpdate={handleAttributionWindowUpdate}
              />
            </div>
          )}
        </div>
        </div>
      </div>
      
      {/* Template Creation Dialog */}
      <CreateTemplateDialog
        isOpen={isTemplateDialogOpen}
        onOpenChange={setIsTemplateDialogOpen}
        restaurantId={user?.restaurants?.[0]?.id}
        organisationId={user?.organisationId}
        onSuccess={handleTemplateCreated}
        initialContent={campaign?.message}
      />
    </Navigation>
  );
};

export default CampaignDetailsPage;